import { Button, ThemeIcon } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import useFetchCommunityFeed from 'src/Hooks/Queries/useFetchCommunityFeed';
import { CommunityFeedControllerCommunityFeedKindEnum } from 'src/Services/API/generated';
import { trackJournalSeeAllClicked, trackJournalViewed } from 'src/Services/Mixpanel/events';

import { communityFeedItemUrl } from '../CommunityFeedItem/communityFeedItemUrl';
import { useCommunityFeedItemFormatContent } from '../CommunityFeedItem/useCommunityFeedItemFormatContent';

import styles from './JournalFeed.module.scss';
import { Props } from './JournalFeed.types';
import { journalTypeToIcon } from './journalTypeToIcon';

/** Nombre de lignes affichées avant de proposer « Tout voir ». */
const COMPACT_SIZE = 7;

const JournalLine = ({
  item,
}: {
  item: Parameters<typeof useCommunityFeedItemFormatContent>[0];
}) => {
  const { jsx: content } = useCommunityFeedItemFormatContent(item);
  const Icon = journalTypeToIcon(item.type);
  const url = communityFeedItemUrl(item);

  const line = (
    <div className={styles.item}>
      <ThemeIcon variant="light" color="teal" size={28} radius="xl">
        <Icon size={16} />
      </ThemeIcon>
      <div>
        <div>{content}</div>
        <span className={styles.date}>{dayjs(item.createdAt).fromNow()}</span>
      </div>
    </div>
  );

  return url ? <Link to={url}>{line}</Link> : line;
};

/**
 * Journal des actions : ce que l'application a enregistré sur le bien, séparé du mur de
 * discussion pour que les messages humains ne soient plus noyés dedans.
 */
const JournalFeed = ({ variant = 'compact' }: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(variant === 'full');

  const { data, fetchNextPage, hasNextPage, isLoading } = useFetchCommunityFeed(
    CommunityFeedControllerCommunityFeedKindEnum.Journal
  );

  useEffect(() => {
    trackJournalViewed({ variant });
  }, [variant]);

  const items = data?.pages.flatMap(page => page.items) ?? [];
  const visibleItems = expanded ? items : items.slice(0, COMPACT_SIZE);

  const handleSeeAll = () => {
    trackJournalSeeAllClicked();
    setExpanded(true);
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>{t('community.journal', 'Journal des actions')}</h3>

      {!isLoading && items.length === 0 && (
        <span className={styles.empty}>
          {t('community.emptyJournal', 'Aucune action enregistrée sur ce bien')}
        </span>
      )}

      <div className={styles.list}>
        {visibleItems.map(item => (
          <JournalLine key={item.id} item={item} />
        ))}
      </div>

      {!expanded && items.length > COMPACT_SIZE && (
        <button type="button" className={styles.seeAll} onClick={handleSeeAll}>
          {t('community.journalSeeAll', 'Tout voir')}
        </button>
      )}

      {expanded && hasNextPage && (
        <Button variant="outline" size="xs" onClick={() => fetchNextPage()}>
          {t('common.loadMore', 'Voir plus')}
        </Button>
      )}
    </div>
  );
};

export default JournalFeed;
